"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { Home, Briefcase, Plus, MessageSquare, User } from "lucide-react";
import { cn } from "@/lib/utils";

interface BottomNavProps {
  userType: "contractor" | "worker";
}

export function BottomNav({ userType }: BottomNavProps) {
  const pathname = usePathname();

  const isContractor = userType === "contractor";

  const links = [
    {
      href: "/dashboard",
      label: "Home",
      icon: Home,
    },
    {
      href: isContractor ? "/contractor/matches" : "/worker/jobs",
      label: "Jobs",
      icon: Briefcase,
    },
    {
      href: isContractor ? "/contractor/post-job" : "/worker/jobs",
      label: "Post",
      icon: Plus,
      isCenter: isContractor,
    },
    {
      href: "/messages",
      label: "Messages",
      icon: MessageSquare,
    },
    {
      href: "/worker/profile/build",
      label: "Profile",
      icon: User,
    },
  ];

  // Workers don't get the center "Post" button — replace with different layout
  const workerLinks = [
    {
      href: "/dashboard",
      label: "Home",
      icon: Home,
    },
    {
      href: "/worker/jobs",
      label: "Jobs",
      icon: Briefcase,
    },
    {
      href: "/messages",
      label: "Messages",
      icon: MessageSquare,
    },
    {
      href: "/worker/profile/build",
      label: "Profile",
      icon: User,
    },
  ];

  const navLinks = isContractor ? links : workerLinks;

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 pb-[env(safe-area-inset-bottom)]">
      <div className="mx-auto flex h-16 max-w-screen-sm items-center justify-around px-2">
        {navLinks.map((link) => {
          const isActive =
            pathname === link.href ||
            (link.href !== "/dashboard" && pathname.startsWith(link.href));
          const Icon = link.icon;

          if ("isCenter" in link && link.isCenter) {
            return (
              <Link
                key={link.href}
                href={link.href}
                className="flex -mt-4 items-center justify-center"
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary shadow-lg active:scale-95 transition-transform">
                  <Icon className="h-7 w-7 text-primary-foreground" />
                </div>
              </Link>
            );
          }

          return (
            <Link
              key={link.href + link.label}
              href={link.href}
              className={cn(
                "flex min-w-[4rem] flex-col items-center gap-0.5 py-2 text-xs transition-colors active:scale-95",
                isActive
                  ? "text-primary font-medium"
                  : "text-muted-foreground"
              )}
            >
              <Icon className="h-5 w-5" />
              <span>{link.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
